import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { toast } from "sonner";
import {
  ArrowLeft, Gift, Copy, Twitter, Send, Zap, BadgeCheck, Crown, Check, Users,
  Flame, Trophy, Sparkles, Share2, Clock,
} from "lucide-react";
import { api } from "@/lib/api";
import { inviteLink } from "@/lib/referral";
import { FoundingShareCard } from "@/components/referrals/FoundingShareCard";
import { ReferralLeaderboard } from "@/components/referrals/ReferralLeaderboard";
import { badgeMeta } from "@/lib/badges";
import { cn } from "@/lib/utils";

const FRIEND_PERK_DAYS = 7;
const FOUNDING_THRESHOLD = 3;
const STREAK_TARGET = 4;

const MILESTONES = [
  { threshold: 3, label: "1 month of BLOQ+, free", tier: "Starter", icon: Zap, accent: "text-bloq-mint" },
  { threshold: 10, label: "Exclusive 'Connector' badge", tier: "Connector", icon: BadgeCheck, accent: "text-bloq-blue" },
  { threshold: 25, label: "1 full year of BLOQ+", tier: "Champion", icon: Crown, accent: "text-bloq-purple" },
];

function useCountdown(endsAt) {
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 60000);
    return () => clearInterval(id);
  }, []);
  if (!endsAt) return null;
  const ms = new Date(endsAt).getTime() - now;
  if (ms <= 0) return null;
  const d = Math.floor(ms / 86400000);
  const h = Math.floor((ms % 86400000) / 3600000);
  const m = Math.floor((ms % 3600000) / 60000);
  return d > 0 ? `${d}d ${h}h` : h > 0 ? `${h}h ${m}m` : `${m}m`;
}

function Stat({ label, value, icon: Icon, accent = "text-white", testid }) {
  return (
    <div className="rounded-2xl border border-white/8 bg-bloq-s1 p-4 text-center" data-testid={testid}>
      <Icon className={cn("mx-auto h-4 w-4", accent)} />
      <div className="mt-1 font-heading text-2xl font-bold text-white">{value}</div>
      <div className="text-[11px] text-bloq-muted">{label}</div>
    </div>
  );
}

function Step({ n, text }) {
  return (
    <li className="flex items-start gap-3">
      <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bloq-gradient text-xs font-bold text-bloq-bg">{n}</span>
      <span>{text}</span>
    </li>
  );
}

function BoostBanner({ boost }) {
  const remaining = useCountdown(boost?.ends_at);
  if (!boost?.active) return null;
  const mult = boost.multiplier || 2;
  return (
    <div className="mt-4 flex items-center gap-3 overflow-hidden rounded-2xl border border-amber-400/40 bg-gradient-to-r from-amber-400/[0.12] to-bloq-mint/[0.08] p-4" data-testid="referral-boost-banner">
      <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-amber-400/20 text-amber-300"><Flame className="h-5 w-5" /></span>
      <div className="flex-1">
        <div className="text-sm font-bold text-white">{mult}× Referral Boost is LIVE</div>
        <div className="text-xs text-bloq-muted">Every verified referral counts {mult}× toward your rewards{remaining ? ` — ends in ${remaining}` : ""}.</div>
      </div>
      {remaining && (
        <span className="flex items-center gap-1 rounded-full bg-amber-400/15 px-2.5 py-1 text-[11px] font-bold text-amber-300" data-testid="referral-boost-countdown">
          <Clock className="h-3 w-3" /> {remaining}
        </span>
      )}
    </div>
  );
}

function FoundingTieIn({ qualified, achieved }) {
  const done = achieved || qualified >= FOUNDING_THRESHOLD;
  return (
    <div className={cn("mt-4 overflow-hidden rounded-3xl border p-5", done ? "border-bloq-mint/50 bg-bloq-mint/[0.08]" : "border-bloq-purple/25 bg-gradient-to-br from-bloq-purple/[0.08] to-bloq-mint/[0.05]")} data-testid="referral-founding-tiein">
      <div className="flex items-center gap-2">
        <span className="inline-flex items-center gap-1 rounded-full bloq-gradient px-2.5 py-0.5 text-[10px] font-black uppercase tracking-wider text-bloq-bg"><Sparkles className="h-3 w-3" /> Founding path</span>
        {done && <span className="rounded-full bg-bloq-mint/15 px-2 py-0.5 text-[10px] font-bold uppercase text-bloq-mint">Unlocked</span>}
      </div>
      <div className="mt-2 text-sm font-semibold text-white">
        {done ? "You've earned Founding Member status 🎉" : `Refer ${FOUNDING_THRESHOLD} verified friends → become a Founding Member`}
      </div>
      <p className="mt-1 text-xs text-bloq-muted">
        {done
          ? "Lifetime 50% off BLOQ+ + the exclusive Founding badge are reserved for you."
          : `Skip the 5,000-spot cap — bring ${FOUNDING_THRESHOLD} verified friends during launch and lock in lifetime 50% off BLOQ+ + the Founding badge.`}
      </p>
      {!done && (
        <div className="mt-3 h-1.5 overflow-hidden rounded-full bg-white/8">
          <div className="h-full rounded-full bloq-gradient" style={{ width: `${Math.min(100, (qualified / FOUNDING_THRESHOLD) * 100)}%` }} />
        </div>
      )}
    </div>
  );
}

function StreakChip({ weeks }) {
  const has = weeks > 0;
  return (
    <div className="mt-4 flex items-center gap-3 rounded-2xl border border-white/8 bg-bloq-s1 p-4" data-testid="referral-streak">
      <span className={cn("flex h-10 w-10 shrink-0 items-center justify-center rounded-xl", has ? "bg-orange-500/15 text-orange-400" : "bg-white/5 text-bloq-muted")}>
        <Flame className="h-5 w-5" />
      </span>
      <div className="flex-1">
        <div className="text-sm font-semibold text-white">{has ? `${weeks}-week referral streak` : "Start a referral streak"}</div>
        <div className="text-xs text-bloq-muted">Refer a friend every week — hit {STREAK_TARGET} weeks for a bonus month of BLOQ+.</div>
      </div>
      {has && <span className="text-lg font-bold text-orange-400" data-testid="referral-streak-weeks">{weeks}🔥</span>}
    </div>
  );
}

export default function ReferralsPage() {
  const navigate = useNavigate();
  const { data } = useQuery({ queryKey: ["referrals-me"], queryFn: api.referralsMe });
  const [friend, setFriend] = useState("");

  const code = data?.code || "";
  const link = inviteLink(code);
  const qualified = data?.referrals_qualified ?? data?.qualified ?? 0;
  const joined = data?.referrals_joined ?? data?.joined ?? qualified;
  const monthsEarned = data?.months_earned ?? 0;
  const streakWeeks = data?.streak_weeks ?? 0;
  const earnedBadges = data?.badges || [];
  const boost = data?.boost;
  const founding = data?.founding;
  const nextMilestone = MILESTONES.find((m) => qualified < m.threshold);
  const highestTier = [...MILESTONES].reverse().find((m) => qualified >= m.threshold);
  const progressPct = nextMilestone ? Math.min(100, (qualified / nextMilestone.threshold) * 100) : 100;

  const copy = (v, msg) => { navigator.clipboard?.writeText(v); toast.success(msg); };
  const shareText = highestTier
    ? `I just hit ${highestTier.tier} on BLOQ Talk — the trust-gated Web3 messenger. Join with my link and get ${FRIEND_PERK_DAYS} days of BLOQ+ free:`
    : `Join me on BLOQ Talk — the trust-gated Web3 messenger. Use my link and get ${FRIEND_PERK_DAYS} days of BLOQ+ free:`;
  const share = (net) => {
    const t = encodeURIComponent(shareText);
    const u = encodeURIComponent(link);
    if (net === "x") window.open(`https://twitter.com/intent/tweet?text=${t}&url=${u}`, "_blank");
    else if (net === "tg") window.open(`https://t.me/share/url?url=${u}&text=${t}`, "_blank");
    else copy(link, "Referral link copied");
  };
  const sendInvite = (e) => {
    e.preventDefault();
    const v = friend.trim();
    if (!v) return;
    const msg = `Join me on BLOQ Talk — the trust-gated Web3 messenger. You'll get ${FRIEND_PERK_DAYS} days of BLOQ+ free: ${link}`;
    window.location.href = v.includes("@")
      ? `mailto:${encodeURIComponent(v)}?subject=${encodeURIComponent("You're invited to BLOQ Talk")}&body=${encodeURIComponent(msg)}`
      : `sms:${encodeURIComponent(v)}?body=${encodeURIComponent(msg)}`;
    setFriend("");
  };

  return (
    <div className="h-full overflow-y-auto bg-bloq-bg" data-testid="referrals-page">
      <div className="mx-auto max-w-2xl px-5 py-8 sm:px-8">
        <div className="flex items-center gap-3">
          <button onClick={() => navigate("/app/profile")} className="rounded-full p-1 text-bloq-muted hover:text-white" data-testid="referrals-back-btn">
            <ArrowLeft className="h-5 w-5" />
          </button>
          <div>
            <h1 className="font-heading text-3xl font-bold tracking-tight">Invite & earn</h1>
            <p className="mt-1 text-sm text-bloq-muted">You give, you get — you both win.</p>
          </div>
        </div>

        <div className="mt-6 overflow-hidden rounded-3xl border border-bloq-mint/25 bg-gradient-to-br from-bloq-mint/[0.08] to-bloq-purple/[0.06] p-6" data-testid="referral-hero">
          <span className="flex h-12 w-12 items-center justify-center rounded-2xl bg-bloq-mint/15 text-bloq-mint"><Gift className="h-6 w-6" /></span>
          <div className="mt-3 text-lg font-bold text-white">Give {FRIEND_PERK_DAYS} days of BLOQ+, earn free months</div>
          <p className="mt-1 text-sm text-bloq-muted">Every friend who joins with your link and verifies their trust gets you closer to free BLOQ+ — and they start with {FRIEND_PERK_DAYS} days of BLOQ+ on the house.</p>
        </div>

        <BoostBanner boost={boost} />

        <div className="mt-4 grid grid-cols-3 gap-3">
          <Stat label="Joined" value={joined} icon={Users} testid="referral-stat-joined" />
          <Stat label="Verified" value={qualified} icon={BadgeCheck} accent="text-bloq-mint" testid="referral-stat-verified" />
          <Stat label="Months earned" value={monthsEarned} icon={Zap} accent="text-bloq-blue" testid="referral-stat-months" />
        </div>

        <FoundingTieIn qualified={qualified} achieved={founding?.achieved} />
        {(founding?.achieved || qualified >= FOUNDING_THRESHOLD) && <FoundingShareCard code={code} qualified={qualified} />}

        {earnedBadges.length > 0 && (
          <div className="mt-4 rounded-3xl border border-white/8 bg-bloq-s1 p-5" data-testid="referral-badges">
            <div className="text-xs font-semibold uppercase tracking-wider text-bloq-muted">Your badges</div>
            <div className="mt-3 flex flex-wrap gap-2">
              {earnedBadges.map((b) => {
                const M = badgeMeta(b);
                const I = M.Icon;
                return (
                  <span key={b} className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-xs font-bold ${M.chip}`} data-testid={`referral-badge-${b}`}>
                    <I className="h-3.5 w-3.5" /> {M.label}
                  </span>
                );
              })}
            </div>
          </div>
        )}

        <ReferralLeaderboard />

        <div className="mt-4 rounded-3xl border border-white/8 bg-bloq-s1 p-5">
          <div className="text-xs font-semibold uppercase tracking-wider text-bloq-muted">Your referral link</div>
          <div className="mt-2 flex items-center gap-2">
            <div className="flex-1 truncate rounded-xl border border-white/12 bg-bloq-bg px-4 py-3 font-mono text-sm text-white" data-testid="referral-link">{link}</div>
            <button onClick={() => copy(link, "Referral link copied")} className="flex h-12 shrink-0 items-center gap-1.5 rounded-xl border border-white/12 px-3 text-sm font-medium text-bloq-mint hover:bg-bloq-mint/10" data-testid="referral-copy-link">
              <Copy className="h-4 w-4" /> Copy
            </button>
          </div>
          {code && (
            <div className="mt-2 text-xs text-bloq-muted">
              Your code:{" "}
              <button onClick={() => copy(code, "Code copied")} className="font-mono tracking-widest text-bloq-mint" data-testid="referral-code">{code}</button>
            </div>
          )}
          <div className="mt-4 flex items-center gap-3">
            <button onClick={() => share("x")} className="flex h-11 w-11 items-center justify-center rounded-full border border-white/12 hover:bg-white/5" data-testid="referral-share-x"><Twitter className="h-5 w-5" /></button>
            <button onClick={() => share("tg")} className="flex h-11 w-11 items-center justify-center rounded-full border border-white/12 hover:bg-white/5" data-testid="referral-share-tg"><Send className="h-5 w-5" /></button>
            <button onClick={() => share("copy")} className="flex h-11 w-11 items-center justify-center rounded-full border border-white/12 hover:bg-white/5" data-testid="referral-share-copy"><Copy className="h-5 w-5" /></button>
          </div>
          <form onSubmit={sendInvite} className="mt-3 flex items-center gap-2" data-testid="referral-invite-form">
            <input value={friend} onChange={(e) => setFriend(e.target.value)} placeholder="Friend's email or phone" className="h-11 flex-1 rounded-xl border border-white/12 bg-bloq-bg px-4 text-sm text-white outline-none placeholder:text-bloq-muted focus:border-bloq-mint/50" data-testid="referral-invite-input" />
            <button type="submit" className="flex h-11 shrink-0 items-center gap-1.5 rounded-xl bloq-gradient px-4 text-sm font-bold text-bloq-bg hover:opacity-90" data-testid="referral-invite-send">
              <Send className="h-4 w-4" /> Invite
            </button>
          </form>
        </div>

        <StreakChip weeks={streakWeeks} />

        {/* Milestone share card */}
        {highestTier && (
          <div className="mt-4 flex items-center gap-3 rounded-3xl border border-bloq-mint/30 bg-bloq-mint/[0.06] p-5" data-testid="referral-share-card">
            <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-bloq-mint/15 text-bloq-mint"><Trophy className="h-5 w-5" /></span>
            <div className="flex-1">
              <div className="text-sm font-bold text-white">You're a BLOQ {highestTier.tier} 🎉</div>
              <div className="text-xs text-bloq-muted">Show it off — sharing brings more verified friends (and more rewards).</div>
            </div>
            <button onClick={() => share("x")} className="flex shrink-0 items-center gap-1.5 rounded-full bloq-gradient px-4 py-2 text-sm font-bold text-bloq-bg hover:opacity-90" data-testid="referral-share-status">
              <Share2 className="h-4 w-4" /> Share
            </button>
          </div>
        )}

        <div className="mt-4 rounded-3xl border border-white/8 bg-bloq-s1 p-5">
          <div className="flex items-center justify-between">
            <div className="text-sm font-bold text-white">Rewards</div>
            {nextMilestone && (
              <div className="text-xs text-bloq-muted" data-testid="referral-next">
                {qualified}/{nextMilestone.threshold} to next{boost?.active ? " · 2× live" : ""}
              </div>
            )}
          </div>
          <div className="mt-3 h-2 overflow-hidden rounded-full bg-white/8">
            <div className="h-full rounded-full bloq-gradient transition-all" style={{ width: `${progressPct}%` }} data-testid="referral-progress" />
          </div>
          <div className="mt-4 space-y-2.5">
            {MILESTONES.map((m) => {
              const done = qualified >= m.threshold;
              const Icon = m.icon;
              return (
                <div key={m.threshold} className={cn("flex items-center gap-3 rounded-2xl border p-3", done ? "border-bloq-mint/40 bg-bloq-mint/[0.06]" : "border-white/8")} data-testid={`referral-milestone-${m.threshold}`}>
                  <span className={cn("flex h-10 w-10 items-center justify-center rounded-xl bg-white/5", done ? "text-bloq-mint" : m.accent)}>
                    {done ? <Check className="h-5 w-5" /> : <Icon className="h-5 w-5" />}
                  </span>
                  <div className="flex-1">
                    <div className="text-sm font-semibold text-white">{m.threshold} verified referrals</div>
                    <div className="text-xs text-bloq-muted">{m.label}</div>
                  </div>
                  {done && <span className="rounded-full bg-bloq-mint/15 px-2 py-0.5 text-[10px] font-bold uppercase text-bloq-mint">Earned</span>}
                </div>
              );
            })}
          </div>
        </div>

        <div className="mt-4 rounded-3xl border border-white/8 bg-bloq-s1 p-5">
          <div className="text-sm font-bold text-white">How it works</div>
          <ol className="mt-3 space-y-2 text-sm text-bloq-muted">
            <Step n={1} text="Share your link with friends who'd value a trust-gated inbox." />
            <Step n={2} text={`They join and verify (reach a minimum BLOQ Score / connect a wallet) — and get ${FRIEND_PERK_DAYS} days of BLOQ+ free.`} />
            <Step n={3} text="You're credited automatically. Hit a milestone, unlock the reward." />
          </ol>
          <p className="mt-3 text-xs text-bloq-muted">Only verified referrals count — it keeps BLOQ Talk clean of fake accounts.</p>
        </div>
      </div>
    </div>
  );
}
